import { AssetSchemaError, ChunkSchemaError, ContractSchemaError } from "daedalus-core";
import { say } from "../words.ts";
import { worldFailure } from "./loading-failure.ts";
import type { LoadingFailure } from "./loading-state.ts";
import { gameReport } from "./report.ts";
import type { ReloadStore } from "./world-version.ts";

/**
 * A page whose build is older than the world it is given. The world loads before the page joins, so a page left open
 * (or served from a cache) across a deploy that moved the compiled schema, the asset schema or the contract's layout
 * meets files it cannot read before the gateway could tell it anything of versions. Such a page reloads, which
 * fetches the page now served: once per world version per tab, the version kept in the tab's `sessionStorage`. When
 * the reload brought the same build back, the loading shell says the game has been updated and offers Reload, and
 * the reader's own message never shows. A world older than the build is not this: no reload mends it.
 */

/**
 * What of the world the build could not read: which schema, the version found and the newest this build reads.
 */
export interface NewerWorld {
  readonly what: "compiled schema" | "asset schema" | "contract schema";
  readonly found: number;
  readonly reads: number;
}

const RELOADED_KEY = "odyssey.older-build";

/**
 * Whether a failed world load says the world was written for a newer build than this page's.
 *
 * @param error What the load threw.
 * @returns What the build could not read, or null for any other failure, a world older than the build among them.
 * @example
 * newerThanBuild(new ChunkSchemaError(5, 4)); // { what: "compiled schema", found: 5, reads: 4 }
 */
export function newerThanBuild(error: unknown): NewerWorld | null {
  if (error instanceof ChunkSchemaError) {
    return error.found > error.expected ? { what: "compiled schema", found: error.found, reads: error.expected } : null;
  }
  if (error instanceof AssetSchemaError) {
    return error.found > error.newest ? { what: "asset schema", found: error.found, reads: error.newest } : null;
  }
  if (error instanceof ContractSchemaError) {
    return error.found > error.expected ? { what: "contract schema", found: error.found, reads: error.expected } : null;
  }
  return null;
}

/**
 * Reloads the page for a world its build cannot read, unless this tab already reloaded for the same world.
 *
 * @param world What names the world: its version, or what the page could not read when it never learned one.
 * @param store The tab's store.
 * @param reload Reloads the page.
 * @returns `reloading`; `held` when the tab already reloaded for this world; or `unavailable` when the store cannot
 *   be used, which reloads nothing, since nothing could bound the loop.
 */
export function reloadForOlderBuild(world: string, store: ReloadStore, reload: () => void): "reloading" | "held" | "unavailable" {
  try {
    if (store.getItem(RELOADED_KEY) === world) {
      return "held";
    }
    store.setItem(RELOADED_KEY, world);
  } catch {
    return "unavailable";
  }
  reload();
  return "reloading";
}

/**
 * The failure for a world newer than the build once the reload was held: the world's title over the sentence a stale
 * page shows everywhere, with Reload, and on the detail line what the page could not read, in words of the page's
 * own.
 *
 * @param newer What the build could not read.
 * @param version The world version, or null when the page never learned it.
 * @returns The failure.
 */
export function olderBuildFailure(newer: NewerWorld, version: string | null): LoadingFailure {
  const world = version === null ? "the world" : `world ${version}`;
  return { category: "world", title: say("loading.failure.world"), sentence: say("close.sentences.gameUpdated"), detail: `${world} is ${newer.what} ${newer.found}; this page reads ${newer.reads}`, action: "reload" };
}

/**
 * What the page does with a world load that failed: reloads once for a world newer than its build, says the game
 * has been updated when that reload was held, and shows any other failure as the world's own.
 *
 * @param error What the load threw.
 * @param version The world version the page was loading, or null when it never learned one.
 * @param store The tab's store.
 * @param reload Reloads the page.
 * @returns `reloading`, after which the page shows nothing more, or the failure to show.
 */
export function meetNewerWorld(error: unknown, version: string | null, store: ReloadStore, reload: () => void): { readonly kind: "reloading" } | { readonly kind: "failed"; readonly failure: LoadingFailure } {
  const newer = newerThanBuild(error);
  if (newer === null) {
    return { kind: "failed", failure: worldFailure(error) };
  }
  if (reloadForOlderBuild(version ?? `${newer.what} ${newer.found}`, store, reload) === "reloading") {
    return { kind: "reloading" };
  }
  return { kind: "failed", failure: olderBuildFailure(newer, version) };
}

/**
 * A store that cannot be used, for a page whose `sessionStorage` throws on access.
 */
const NO_STORE: ReloadStore = {
  getItem: () => {
    throw new Error("no store");
  },
  setItem: () => {
    throw new Error("no store");
  },
  removeItem: () => {
    throw new Error("no store");
  },
};

/**
 * `meetNewerWorld` on this page: the tab's `sessionStorage` and a real reload, with the reason kept in the report.
 *
 * @param error What the world's load threw.
 * @param version The world version the page was loading, or null.
 * @returns The failure to show, or null when the page is reloading.
 */
export function worldLoadFailure(error: unknown, version: string | null): LoadingFailure | null {
  let store: ReloadStore;
  try {
    store = window.sessionStorage;
  } catch {
    store = NO_STORE;
  }
  const outcome = meetNewerWorld(error, version, store, () => window.location.reload());
  if (outcome.kind === "reloading") {
    gameReport.closed = `the world is newer than this page (${error instanceof Error ? error.message : String(error)}); reloading`;
    return null;
  }
  return outcome.failure;
}
